import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { isTokenExpired } from '@/utils/isTokenExpired'
import { resetClient } from '@/composables/useClient'
import { jwtDecode } from 'jwt-decode'
import dayjs from 'dayjs'
import { authMutations } from '@/queries/auth'
import { HttpError } from '@/queries/auth-error'
import { queryClient } from '@/queries/queryClient'
import { useAnalytics } from '@/composables/useAnalytics'
import { accountQueries } from '@/queries/account'

export const useAuthStore = defineStore('auth', () => {
  const accessToken = ref<string | null>()
  const isAuthenticated = computed(() => !!accessToken.value)
  const lastTokenCheck = ref(0)
  const getAccessToken = async (): Promise<string | undefined> => {
    return accessToken.value || undefined
  }

  const identifyUser = async (): Promise<void> => {
    try {
      const user = await queryClient.fetchQuery(accountQueries.profile())

      useAnalytics().identify(user.cognitoUserId, {
        email: user.email,
        first_name: user.firstName,
        last_name: user.lastName,
        phone: user.phoneNumber,
        tenant: user.tenantType,
      })
    } catch (error) {
      console.error('Failed to identify analytics user', error)
    }
  }

  const setAccessToken = async (token: string) => {
    accessToken.value = token
    await identifyUser()
  }

  const removeAccessToken = () => {
    accessToken.value = null
    useAnalytics().reset()
  }

  const refreshToken = async (): Promise<void> => {
    try {
      const response = await authMutations.refresh().mutationFn()
      await setAccessToken(response.accessToken)
    } catch (error) {
      if (error instanceof HttpError) {
        removeAccessToken()
        return
      }

      throw error
    }
  }

  const logout = async (): Promise<void> => {
    try {
      await authMutations.logout().mutationFn()
    } finally {
      queryClient.clear()
      removeAccessToken()
      resetClient()
    }
  }

  const checkIfTokenNeedRefresh = async (): Promise<void> => {
    const token = accessToken.value

    if (!token || isTokenExpired(token)) {
      console.debug("No access token or it's expired, attempting to refresh from cookie")
      await refreshToken()
    }
  }

  const checkIfTokenExpired = async (): Promise<void> => {
    const token = accessToken.value
    if (!token) return

    const now = dayjs()

    try {
      const decoded = jwtDecode<{ exp: number }>(token)
      const expired = dayjs.unix(decoded.exp)
      const expiredMinusTwoMin = expired.subtract(2, 'minute')

      if (dayjs().isAfter(expiredMinusTwoMin)) {
        console.debug('Access token is expired, refreshing token...')
        await refreshToken()
      }
    } catch {
      console.error('Failed to decode access token')
    }
    lastTokenCheck.value = now.valueOf()
  }

  return {
    isAuthenticated,
    checkIfTokenNeedRefresh,
    checkIfTokenExpired,
    getAccessToken,
    setAccessToken,
    removeAccessToken,
    refreshToken,
    logout,
  }
})
